import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import CurrentReportsSitrepCard from 'components/Reports/CurrentReportsSitrepCard';
import SitrepToggleSection from 'components/Reports/SitrepToggleSection';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { NULL_VALUE } from 'constants/displayConstants';
import { useOptionalIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { useExportedFormFiles } from 'components/FileManagement/hooks/useExportedFormFiles';
import { useSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';
import useDownloadFile from 'components/FileManagement/hooks/useDownloadFile';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { toast } from 'react-toastify';
import type { Geometry } from 'geojson';

export type IncidentListItemData = {
  id: string;
  name: string;
  aor: string;
  incidentTypes: string[];
  aorIds: string[];
  createdAt: string;
  latLong: { latitude: number; longitude: number } | null;
  locationGeometry: Geometry | null;
  currentPeriodId?: string;
};

type IncidentListItemProps = {
  incident: IncidentListItemData;
  currentOperationalPeriod: string;
  currentPeriodNumber: number;
  incidentCommander: string;
  sitrep: GetSitrepsResponse | null;
  showWorkspaceButton?: boolean;
  onWorkspaceClick?: () => void;
};

export const IncidentListItem: React.FC<IncidentListItemProps> = ({
  incident,
  currentOperationalPeriod,
  currentPeriodNumber,
  incidentCommander,
  sitrep,
  showWorkspaceButton = false,
  onWorkspaceClick,
}) => {
  const icsFormPdf = useOptionalIcsFormPdfPreview();
  const { data: exportedForms = [] } = useExportedFormFiles(incident.id);
  const { mutateAsync: getSignedUrl } = useSignedUrl();
  const downloadFile = useDownloadFile();

  const approvedPlanFile = useMemo(() => {
    const isInitialOrTransitionPeriod = currentPeriodNumber <= 1;

    if (isInitialOrTransitionPeriod) {
      const regex201 = /ICS[-_ ]?201/i;
      const regexOp0 = /OP[-_ ]?0/i;
      return exportedForms
        .filter(
          (file) =>
            regex201.test(file.file_name) && regexOp0.test(file.file_name)
        )
        .sort((a, b) => dayjs(b.updatedAt).diff(a.updatedAt))
        .at(0);
    }

    const exportedIAPPattern = new RegExp(
      '^IAP-OP' + currentPeriodNumber + '-'
    );
    return exportedForms
      .filter((file) => file.file_name.match(exportedIAPPattern))
      .sort((a, b) => dayjs(b.updatedAt).diff(a.updatedAt))
      .at(0);
  }, [currentPeriodNumber, exportedForms]);

  const handlePreviewActiveDocument = async () => {
    if (!icsFormPdf) return;

    if (!approvedPlanFile) {
      toast.info('No approved plan found for this operational period.');
      return;
    }

    try {
      const signedUrl = await getSignedUrl(approvedPlanFile.fileUrl);
      icsFormPdf.previewFromSignedUrl({
        fileUrl: signedUrl,
        fileName: approvedPlanFile.file_name,
        onDownload: () => {
          void downloadFile(approvedPlanFile);
        },
      });
    } catch {
      toast.error('Failed to load approved plan preview.');
    }
  };

  return (
    <div className="d-grid gap-2 p-3">
      {showWorkspaceButton && onWorkspaceClick && (
        <div className="d-flex align-items-center gap-2">
          <StylishNewButton
            className="button--secondary"
            onClick={onWorkspaceClick}
          >
            Incident Workspace
          </StylishNewButton>
        </div>
      )}

      <div className="row gx-4 gy-2">
        <div className="col-lg-6 d-grid col-12 gap-2">
          <div>
            <div className="fw-semibold">Incident Types</div>
            <div>{incident.incidentTypes.join(', ') || NULL_VALUE}</div>
          </div>
          <div>
            <div className="fw-semibold">AOR</div>
            <div>{incident.aor}</div>
          </div>
          <div>
            <div className="fw-semibold">Incident Creation Time</div>
            <div>{incident.createdAt}</div>
          </div>
        </div>

        <div className="col-lg-6 d-grid col-12 gap-2">
          <div>
            <div className="fw-semibold">Current Operational Period</div>
            <div>{currentOperationalPeriod}</div>
            <button
              type="button"
              className="btn btn-link text-decoration-underline fw-semibold p-0"
              onClick={() => {
                void handlePreviewActiveDocument();
              }}
              disabled={!icsFormPdf}
            >
              Approved plan for this op period
            </button>
          </div>
          <div>
            <div className="fw-semibold">Incident Commander</div>
            <div>{incidentCommander}</div>
          </div>
        </div>
      </div>

      {sitrep && (
        <SitrepToggleSection>
          <div className="overflow-hidden">
            <CurrentReportsSitrepCard sitrep={sitrep} />
          </div>
        </SitrepToggleSection>
      )}
    </div>
  );
};
